import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

const FeaturesContext = createContext();

export const useFeatures = () => {
  const context = useContext(FeaturesContext);
  if (!context) {
    throw new Error('useFeatures must be used within a FeaturesProvider');
  }
  return context;
};

// fn_get_my_features returns a flat object: { "dashboard.booking": true, ... }.
// The app routes + in-page toggles use unprefixed feature names (e.g. "bookings",
// "cart_email") — translate those to the entitlement keys Backend actually
// writes. Kept aligned with Part 9's mapping table.
//
// 2026-07-30 — added `cart_email → dashboard.recovery` (Recovery button was
// invisible for everyone because the legacy key never resolved) and corrected
// `bookings → dashboard.booking` (was `dashboard.booking_engine`, wrong flag
// per Part 9 §2). Also filled in the cdp / event / CI mappings that were
// commented out before Part 9 registered them.
const ROUTE_TO_ENTITLEMENT_KEY = {
  bookings: 'dashboard.booking',
  google_ads: 'dashboard.google_ads',
  google_analytics: 'dashboard.google_analytics',
  aim: 'dashboard.aim',
  route_intelligence: 'dashboard.route_intelligence',
  cdp: 'dashboard.cdp',
  event_intelligence: 'dashboard.event_intelligence',
  competitive_intelligence: 'dashboard.competitive_intelligence',
  cart_email: 'dashboard.recovery',
  session_details: 'feature.session_details',
  // 2026-08-13 — Search Console + Weather Intelligence. Per-property flags;
  // the exact string is matched literally against the database row. See the
  // feature-gating briefs for each page.
  search_console: 'dashboard.search_console',
  weather_intelligence: 'dashboard.weather_intelligence',
  local_intelligence: 'dashboard.local_intelligence',
};

export const FeaturesProvider = ({ children }) => {
  const { session, loading: authLoading } = useAuth();
  const [entitlements, setEntitlements] = useState({});
  const [loading, setLoading] = useState(true);
  // 2026-07-13 — only show the loading state on the FIRST fetch. Supabase
  // Auth's `TOKEN_REFRESHED` fires on tab-focus, which re-runs this effect;
  // toggling loading=true would unmount <FeatureRoute>-gated pages
  // (they return null while loading), lose per-page useRef state (e.g.
  // "date-defaulted-once" guards), and remount with fresh defaults. Refetch
  // silently on subsequent runs so pages stay mounted across auth refreshes.
  const hasFetchedOnce = useRef(false);

  // 2026-08-13 — Per-property feature flags (search_console, weather_intelligence)
  // require re-fetching when the selected property changes. FeaturesProvider wraps
  // ViewProvider in the tree, so we can't useView() here. Instead, ViewContext
  // calls refreshFeatures(clientId) when selectedClientId changes. The clientId
  // is passed as p_client_id to fn_get_my_features so the backend resolves the
  // flag map for that specific property.
  const fetchFeatures = useCallback(async (clientId) => {
    if (!session) {
      setEntitlements({});
      setLoading(false);
      return;
    }
    const showLoader = !hasFetchedOnce.current;
    if (showLoader) setLoading(true);
    try {
      const rpcParams = {};
      if (clientId) rpcParams.p_client_id = clientId;
      const { data, error } = await supabase.rpc('fn_get_my_features', rpcParams);
      if (error) throw error;
      setEntitlements(data && typeof data === 'object' && !Array.isArray(data) ? data : {});
    } catch (err) {
      console.error('fn_get_my_features error:', err.message);
      setEntitlements({});
    } finally {
      hasFetchedOnce.current = true;
      if (showLoader) setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (authLoading) return;
    fetchFeatures();
  }, [authLoading, fetchFeatures]);

  const hasFeature = useCallback((featureKey) => {
    if (!featureKey) return false;
    if (entitlements[featureKey] === true) return true;
    const mapped = ROUTE_TO_ENTITLEMENT_KEY[featureKey];
    if (mapped && entitlements[mapped] === true) return true;
    return false;
  }, [entitlements]);

  const refreshFeatures = useCallback((clientId) => fetchFeatures(clientId), [fetchFeatures]);

  // 2026-08-13 — Per the feature-gating briefs: warn in development when a
  // per-property flag key is absent from the response (not just false). Absent
  // means it was never registered in the database — a different bug from
  // disabled. Only warn in dev; stay silent in production.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    if (!entitlements || Object.keys(entitlements).length === 0) return;
    const perPropertyKeys = ['dashboard.search_console', 'dashboard.weather_intelligence', 'dashboard.local_intelligence'];
    for (const key of perPropertyKeys) {
      if (!(key in entitlements)) {
        console.warn(`[FeaturesContext] ${key} is not registered in feature_flags — the flag was never set up on the backend. The page will be hidden. This is a registration issue, not a gating issue.`);
      }
    }
  }, [entitlements]);

  // Preserve the legacy `features[key]` shape for any consumer still reading it
  // as a flat object of enabled flags.
  const features = useMemo(() => {
    const obj = {};
    for (const [k, v] of Object.entries(entitlements)) {
      if (v === true) obj[k] = true;
    }
    return obj;
  }, [entitlements]);

  return (
    <FeaturesContext.Provider value={{ features, entitlements, hasFeature, loading, refreshFeatures }}>
      {children}
    </FeaturesContext.Provider>
  );
};
